/**
 * Runtime Guards - DEV-only utilities for catching common bugs
 *
 * These guards help detect:
 * - NaN values in calculations (usually from undefined/null)
 * - Double-calls in same frame (stale closure issues)
 * - Missing required state
 *
 * All guards are no-ops in production for performance.
 */

const isDev = process.env.NODE_ENV === 'development';

// Track calls to detect double-invocations
const callTracker = new Map();

/**
 * Guard against NaN values in critical calculations
 * @param {number} value - The value to check
 * @param {string} label - Description for error message
 * @returns {number} The original value, or 0 if NaN (with console error)
 */
export function guardNaN(value, label) {
  if (isDev && (Number.isNaN(value) || value === undefined || value === null)) {
    console.error(`[GUARD] NaN/undefined detected in ${label}:`, value);
    return 0; // Safe fallback
  }
  return value;
}

/**
 * Detect double-calls within same frame (indicates stale closure)
 * @param {string} key - Unique identifier for the call site
 */
export function guardDoubleCall(key) {
  if (!isDev) return;

  const now = Date.now();
  const last = callTracker.get(key);

  if (last && now - last < 16) {
    // Same frame (~60fps = 16.67ms)
    console.warn(`[GUARD] Double call detected: ${key}`);
  }
  callTracker.set(key, now);
}

/**
 * Guard against missing required state
 * @param {*} value - The value to check
 * @param {string} label - Description for warning message
 * @returns {*} The original value (unchanged)
 */
export function guardMissing(value, label) {
  if (isDev && (value === null || value === undefined)) {
    console.warn(`[GUARD] Missing state: ${label}`);
  }
  return value;
}

/**
 * Guard against negative resource values
 * @param {number} value - The value to check
 * @param {string} label - Description for warning message
 * @returns {number} The original value clamped to 0 if negative
 */
export function guardNegative(value, label) {
  if (isDev && value < 0) {
    console.warn(`[GUARD] Negative value in ${label}:`, value);
  }
  return Math.max(0, value);
}

/**
 * Clear call tracker (useful for testing)
 */
export function clearCallTracker() {
  callTracker.clear();
}
